import * as React from 'react';
import {
  maxFileSize,
  supportedFileExtentions,
} from 'config';
import * as _ from 'lodash';

import { Grid, Icon, Checkbox, Divider, Form, Modal, Image } from 'semantic-grab-ui/src';
import FormInputWithError from 'components/form/FormInputWithError';
import LocalizedMenu from 'components/form/LocalizedMenu';
import ImageUpload from 'components/form/ImageUpload';

import { Campaign } from 'types/ts/campaigns';

import './OffersDiscoveryDetails.scss';

const handleUpdateLanguages = (model: Campaign, onModelChange: Function) => (langs: Array<string>) => {
  const offersData = model.offersData || {};
  const updatedModel = { ...model, offersData: { ...offersData, localizedName: {}, localizedTnC: {} } };
  _.forEach(langs, (lang) => {
    updatedModel.offersData.localizedName[lang] = model.offersData?.localizedName?.[lang] || '';
    updatedModel.offersData.localizedTnC[lang] = model.offersData?.localizedTnC?.[lang] || '';
  });
  onModelChange(updatedModel);
};

const handleImageChange = (model: Campaign, onModelChange: Function, type: string) => (image: any) => {
  const offersData = model.offersData || {};

  if (!image) {
    const updatedModel = { ...model, offersData: { ...offersData, [type]: {} } };
    onModelChange(updatedModel);
    return;
  }

  const formattedImage = Object.assign({}, image, { type });
  const updatedModel = { ...model, offersData: { ...offersData, [type]: formattedImage } };
  onModelChange(updatedModel);
};

const renderLocalizedContent = (languages: Array<string>, model: Campaign, handleInputChange: Function, handleControlBlur: Function, error: any, hasError: Function) => {
  const localizedContents = _.reduce(languages, (result, language) => {
    const content = (
      <span>
        <FormInputWithError
          name={`offersData.localizedName.${language}`}
          error={error}
          hasError={hasError}
        >
          <Form.Input
            required
            label={<div className="required field" style={{ marginBottom: "0.57rem", lineHeight: "1em" }}>
              <label style={{ marginBottom: 0 }}> Offer Title </label>
              <span style={{ color: "#898d97", fontSize: "0.85em" }}>
                Maximum of 30 characters
              </span>
            </div>}
            placeholder="$ or % off your next purchase"
            value={model.offersData?.localizedName?.[language] || ''}
            onChange={handleInputChange}
            onBlur={handleControlBlur}
            maxLength="30"
            style={{ marginBottom: 14, width: 250 }}
          />
        </FormInputWithError>

        <FormInputWithError
          name={`offersData.localizedTnC.${language}`}
          error={error}
          hasError={hasError}
        >
          <Form.TextArea
            required
            label="Terms and Conditions"
            value={model.offersData?.localizedTnC?.[language] || ''}
            onChange={handleInputChange}
            onBlur={handleControlBlur}
            style={{ width: 500, height: 120 }}
          />
        </FormInputWithError>
      </span>
    );

    return Object.assign({}, result, { [language]: content });
  }, {});
  return localizedContents;
};

type OffersDiscoveryDetailsProps = {
  model: Campaign,
  onModelChange: Function,
  handleInputChange: Function,
  handleControlBlur: Function,
  error: any,
  hasError: Function,
};

const OffersDiscoveryDetails = (props: OffersDiscoveryDetailsProps) => {
  const {
    model,
    onModelChange,
    handleInputChange,
    handleControlBlur,
    error,
    hasError,
  } = props;

  const maxSize = {
    logoImage: [260, 260],
    coverImage: [1242, 464],
  };

  let languages = _.keys(model.offersData?.localizedName);
  languages = languages.length > 0 ? languages : ['en'];

  const localizedContents = renderLocalizedContent(languages, model, handleInputChange, handleControlBlur, error, hasError);

  return (
    <div>
      <Grid>
        {model.targetUserType == "passenger" &&
          <Grid.Column width={8}>
            <Checkbox
              name="offersData.enableOfferDiscovery"
              label="Discoverable on the Grab app"
              checked={model.offersData?.enableOfferDiscovery}
              onChange={handleInputChange}
            />
            <OffersDiscoveryExample />
          </Grid.Column>
        }
      </Grid>

      <Grid>
        <Grid.Column width={12}>
          {
            model.offersData?.enableOfferDiscovery &&
            <div>
              <LocalizedMenu
                languages={languages}
                contents={localizedContents}
                showAddLanguage
                onUpdateLanguages={handleUpdateLanguages(model, onModelChange)}
              />

              <Divider />

              <ImageUpload
                medium={model.offersData?.logoImage}
                title="Logo Image"
                formName="NewTridentOffersLogo"
                description="If left empty, logo image will default to the same one used in the awarded reward issued for next purchase"
                maxFileSize={maxFileSize}
                maxImageSize={maxSize.logoImage}
                supportedFileExtentions={supportedFileExtentions}
                onImageChange={handleImageChange(model, onModelChange, 'logoImage')}
              />

              <Divider />

              <ImageUpload
                medium={model.offersData?.coverImage}
                title="Cover Image"
                formName="NewTridentOffersCover"
                description="If left empty, cover image will default to the same one used in the awarded reward issued for next purchase"
                maxFileSize={maxFileSize}
                maxImageSize={maxSize.coverImage}
                supportedFileExtentions={supportedFileExtentions}
                onImageChange={handleImageChange(model, onModelChange, 'coverImage')}
              />
            </div>

          }

        </Grid.Column>
      </Grid>
    </div>);
};

const OffersDiscoveryExample = () => {
  return (
    <Modal
      centered
      size='large'
      trigger={<Icon name="question circle outline" className="questionIcon" />}>
      <Modal.Header>Discoverable on the Grab app</Modal.Header>
      <Modal.Content>
        <div style={{ marginBottom: 16 }}>
          This is specifically for pay-to-merchant next purchase rewards.
          Checking this box will require additional inputs to surface relevant information
          about this Trident campaign, as an offer, on various customer-facing screens.
          See example below.
        </div>
        <Image wrapped src={require('img/discoverable_legend.jpg')} />
      </Modal.Content>
    </Modal>
  )
}
export default OffersDiscoveryDetails;
